<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="rootElement"
    :class="`relative @container text-foreground ${panelShown ? 'z-100' : ''}`"
    @focusin="hasFocus = true"
    @focusout="onFocusOut">
    <!-- Same dim + blur as the js-lib Backdrop the cart drawer and dialogs use. Can't reuse the
         component itself: it's a headless-ui TransitionChild and needs a TransitionRoot parent. -->
    <Transition name="fade">
      <div v-if="panelOpen" class="fixed z-1 inset-0 bg-black/35 backdrop-blur-xs" @click="close"></div>
    </Transition>

    <!-- Holds the header's layout while the panel above it grows over the page. -->
    <div class="h-11"></div>

    <!-- Search panel: closed it is just the input bar, open it expands into a card that
         keeps the bar at its top and scrolls the results underneath it. On mobile there is no
         room for a floating card, so it takes over the whole screen instead. -->
    <div
      class="z-10 flex flex-col group transition-all duration-200 ease-out"
      :class="
        !panelShown
          ? 'absolute inset-x-0 top-0'
          : isDesktop
            ? 'absolute search-panel -inset-x-3 -top-3 bg-surface rounded-2xl p-3 shadow-2xl'
            : 'fixed inset-0 bg-surface p-3'
      ">
      <div class="flex-none flex items-center gap-2">
        <!-- Full screen on mobile hides the backdrop, so this is the way out there. -->
        <button
          v-if="panelShown && !isDesktop"
          type="button"
          :aria-label="t('Close')"
          class="flex-none flex size-11 items-center justify-center text-foreground"
          @mousedown.prevent
          @click="close">
          <ChevronLeftIcon class="size-7 text-foreground" />
        </button>
        <!-- Search input bar -->
        <div
          class="flex-1 min-w-0 flex items-center gap-2 rounded-xl border-[0.5px] border-input bg-accent hover:bg-surface-selected transition h-11">
          <!-- AI mode toggle. Hidden once the classic panel opens: the CTA at the bottom of that
             panel takes over from there. In AI mode the panel never opens, so the toggle stays put
             and remains the only way back to the classic search. mousedown.prevent keeps focus off
             it, so clicking the toggle flips it instead of opening the panel underneath. -->
          <AiGlowBorder v-if="showAiModeOption && !panelShown" class="flex-none h-full rounded-xl">
            <div
              role="switch"
              :aria-checked="aiMode"
              :aria-label="t('AI-mode')"
              class="relative flex items-center gap-2 h-full px-1.5 rounded-xl bg-surface text-foreground cursor-pointer select-none"
              @mousedown.prevent
              @click="aiMode = !aiMode">
              <AiModeIcon class="size-5 flex-none" />
              <span class="hidden @sm:inline text-base font-medium whitespace-nowrap">{{ t("AI-mode") }}</span>
              <Switch v-model="aiMode" label="" class="pointer-events-none flex items-center" />
            </div>
          </AiGlowBorder>
          <!-- Without the AI-mode toggle the bar has nothing marking it as search, so an icon takes its place. -->
          <SearchIcon v-if="!showAiModeOption" class="flex-none size-5 ml-3 text-muted-foreground" aria-hidden="true" />
          <input
            v-model="query"
            name="search"
            type="text"
            enterkeyhint="search"
            maxlength="75"
            autocorrect="off"
            spellcheck="false"
            autocomplete="off"
            :aria-label="t('Search')"
            :placeholder="placeholder"
            class="flex-1 min-w-0 h-11 text-base px-3 placeholder-muted-foreground outline-none border-none ring-0 focus:ring-0 focus:outline-none appearance-none bg-transparent"
            @keydown.enter="onSearchKey" />
          <button
            v-if="query"
            type="button"
            :aria-label="t('Close')"
            class="group/clear flex-none flex size-11 items-center justify-center cursor-pointer"
            @click="close">
            <CancelIcon
              class="size-4 text-foreground group-hover/clear:text-error group-hover/clear:scale-105 transition" />
          </button>
        </div>
      </div>

      <Transition name="panel" @after-leave="onPanelLeft">
        <!-- The content is inset px-3 on top of the panel's p-3; without the AI-mode bar to close it
             off, pb-3 gives the bottom that same inset. On mobile, mt-3 mirrors the p-3 above the bar
             so scrolled content stops short of it instead of running right up against it. -->
        <div
          v-if="panelOpen"
          class="flex-1 min-h-0 flex flex-col"
          :class="{ 'pb-3': !showAiModeOption, 'mt-3': !isDesktop }">
          <!-- Nothing typed yet: offer somewhere to start rather than an empty card. Shares the
             results block's mousedown/touch guards so a click here does not blur the input first. -->
          <div
            v-show="!typedTerm"
            class="flex-1 overflow-y-auto px-3 pt-6 flex flex-col gap-6"
            @mousedown.prevent
            @touchstart="pendingResultClick = true"
            @touchend="pendingResultClick = false">
            <div v-if="suggestionsLoading" class="flex flex-col gap-6" data-testid="suggested-searches-loading">
              <span class="text-sm font-medium text-muted-foreground">{{ t("Upcoming events & tournaments") }}</span>
              <div class="grid gap-x-6 gap-y-8 @xl:grid-cols-2">
                <Skeleton v-for="index in 8" :key="index" variant="rounded" class="h-5 w-3/4 rounded-sm" />
              </div>
              <span class="sr-only">{{ t("Loading...") }}</span>
            </div>
            <div v-else-if="suggestedSearches.length" class="flex flex-col gap-6">
              <span class="text-sm font-medium text-muted-foreground">{{ t("Upcoming events & tournaments") }}</span>
              <!-- Row-major, so the two tournaments, which come first, top both columns and the events fill
                 the rows under them. Two equal columns, the same as the featured sports below, so both
                 lists line up. A name too long for one line wraps beside its pill, which stays right-aligned
                 and centred on it, rather than pushing the panel into a sideways scroll. -->
              <div class="grid grid-cols-1 gap-x-6 gap-y-8 @xl:grid-cols-2">
                <RouterLink
                  v-for="suggestion in suggestedSearches"
                  :key="suggestion.name"
                  :to="suggestion.to"
                  class="min-w-0 flex items-center justify-between gap-2 text-start transition font-semibold text-sm">
                  <span
                    class="flex-1 min-w-0 break-words hover:underline decoration-black decoration-1 underline-offset-4"
                    >{{ suggestion.name }}</span
                  >
                  <span class="flex-none bg-background text-xs font-medium px-2.5 py-0.5 rounded-sm">
                    {{ t(suggestion.type) }}
                  </span>
                </RouterLink>
              </div>
            </div>

            <template v-if="featuredSports.length">
              <hr v-if="suggestionsLoading || suggestedSearches.length" />
              <div class="flex flex-col gap-6">
                <span class="text-sm font-medium text-muted-foreground">{{ t("Featured sports") }}</span>
                <div class="grid @xl:grid-cols-2 gap-x-6 gap-y-8">
                  <RouterLink
                    v-for="sport in featuredSports"
                    :key="sport.key"
                    :to="toEvents(sport.key)"
                    class="flex items-center gap-2 transition font-semibold text-sm">
                    <component :is="sport.icon" class="size-5 flex-none text-foreground" />
                    <span class="hover:underline decoration-black decoration-1 underline-offset-4">{{
                      t(sport.name)
                    }}</span>
                  </RouterLink>
                </div>
              </div>
            </template>
          </div>

          <!-- Search results: a tab per result type, with "All" stacking every type as its own
             section. Tabs sit outside the scroll area so they stay put while the results scroll. -->
          <div
            v-show="typedTerm"
            ref="resultsWrapperElement"
            class="flex-1 min-h-0 flex flex-col"
            @mousedown.prevent
            @touchstart="pendingResultClick = true"
            @touchend="pendingResultClick = false">
            <template v-if="!isLoading">
              <template v-if="resultGroups.length">
                <!-- The base line is an inset shadow rather than a border: overlapping a border would need a
                     negative margin, which overflows the scroll container and brings up a scrollbar. -->
                <div
                  role="tablist"
                  class="flex-none flex gap-6 mx-3 mt-4 shadow-[inset_0_-1px_0_var(--color-border)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    role="tab"
                    :aria-selected="activeTab === tab.key"
                    class="flex-none pb-3 border-b-[5px] text-base whitespace-nowrap transition"
                    :class="
                      activeTab === tab.key
                        ? 'border-foreground text-foreground font-semibold'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    "
                    @click="activeTab = tab.key">
                    {{ t(tab.label) }} <span class="text-xs text-muted-foreground">({{ tab.count }})</span>
                  </button>
                </div>

                <!-- A single list tab starts right under the tab line, so a hovered first row meets it
                     without a gap. "All" (section headings) and "Events" (cards) keep their space above. -->
                <div
                  class="flex-1 min-h-0 overflow-y-auto px-3 flex flex-col gap-8"
                  :class="{ 'pt-6': activeTab === 'all' || activeTab === 'events' }">
                  <section v-for="group in visibleGroups" :key="group.key" class="flex flex-col">
                    <div
                      v-if="activeTab === 'all'"
                      class="flex items-center justify-between gap-3 pb-3 border-b border-border"
                      :class="{ 'mb-3': group.key === 'events' }">
                      <h3 class="flex items-center gap-2 text-base font-semibold">
                        <component :is="group.icon" class="size-5 flex-none text-foreground" />
                        {{ t(group.label) }}
                      </h3>
                      <button
                        v-if="group.count > ALL_TAB_LIMIT"
                        type="button"
                        class="text-sm font-medium text-foreground underline hover:no-underline decoration-1 underline-offset-4"
                        @click="activeTab = group.key">
                        {{ t("View all") }} ({{ group.count }})
                      </button>
                    </div>
                    <div v-if="group.key === 'events'" class="flex flex-col gap-3">
                      <EventCard v-for="event in limitForTab(eventCards)" :key="event.event_id" :event="event" small />
                    </div>
                    <!-- One row per result: name, a line of context under it. The
                         13px inset is the event card's 1px border plus its p-3, so all text lines up. -->
                    <ul v-else class="flex flex-col divide-y-[0.5px] divide-border border-b-[0.5px] border-border">
                      <li v-for="result in limitForTab(group.results)" :key="result.name">
                        <RouterLink
                          :to="result.to"
                          class="flex items-center justify-between gap-3 py-3 px-3.25 hover:bg-accent transition">
                          <span class="flex flex-col gap-1 min-w-0">
                            <span class="flex items-center gap-2">
                              <svg
                                v-if="result.colorsSvg"
                                class="size-5 rounded-full overflow-hidden border border-gray-300 flex-none"
                                v-html="result.colorsSvg"></svg>
                              <span class="text-base font-semibold text-foreground">{{ result.name }}</span>
                            </span>
                            <span v-if="result.subtitle" class="text-sm text-muted-foreground">{{
                              result.subtitle
                            }}</span>
                          </span>
                          <ArrowRightIcon class="size-5 flex-none text-foreground" />
                        </RouterLink>
                      </li>
                    </ul>
                  </section>
                </div>
              </template>
              <span v-else class="px-3 pt-6">{{ $t("No results found") }}</span>
            </template>
            <div v-else class="flex-1 px-3 pt-6 flex flex-col gap-2 animate-pulse">
              <div v-for="n in 4" :key="n" class="bg-accent rounded h-12"></div>
              <div v-for="n in 4" :key="`s${n}`" class="bg-accent rounded h-4"></div>
            </div>
          </div>

          <!-- AI mode call to action, pinned to the bottom of the panel. Gone once AI mode is on:
             it only exists to offer the mode, so it has nothing left to say. The content above has no
             bottom padding -- the panel's p-3 matches the space above the bar -- so the gap is its own. -->
          <button
            v-if="showAiModeOption"
            type="button"
            role="switch"
            :aria-checked="aiMode"
            class="relative flex-none w-full mt-6 flex items-center justify-between gap-3 h-11 px-5 rounded-xl border-[0.5px] border-border bg-surface hover:bg-accent transition"
            @click="aiMode = !aiMode">
            <span class="flex items-center gap-3 text-muted-foreground text-sm">
              <AiModeIcon class="size-5 flex-none" />
              {{ t("Not sure what to search for?") }}
            </span>
            <span class="font-medium whitespace-nowrap text-sm">{{ t("Use our AI-mode") }}</span>
          </button>
        </div>
      </Transition>
    </div>

    <!-- AI mode's own surface, in place of the classic panel. Dismissing it leaves the toggle
         on, so focusing the bar again brings it straight back; only the toggle turns AI mode off. -->
    <AiModeDialog v-if="aiDialogOpen" @close="closeAiDialog" @submit="onAiPrompt" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type Component } from "vue";
import { useI18n } from "vue-i18n";
import { useTeams, useCities, useVenues, useEventsSearch } from "@xs2event/js-lib/api";
import { PORTAL_TOURNAMENTS_CACHE_STALE_TIME, usePortalTournamentsCache } from "@/composables/tournaments";
import { useCachedEventsByIds } from "@/composables/events";
import { RouterLink } from "vue-router";
import { todayISODate } from "@/helpers/format";
import { EventCard } from "@/components/event";
import AiModeDialog from "./AiModeDialog.vue";
import { Skeleton, Switch } from "@xs2event/js-lib/components";
import { ChevronLeftIcon, arrowRight as ArrowRightIcon, search as SearchIcon } from "@xs2event/js-lib/icons";
import { lookupSportTypeName } from "@/store/sportTypes";
import { lookupCountryName } from "@/store/locations";
import { camelToSnake, snakeToCamel } from "@/helpers/casing";
import brandconfig from "@/brandconfig";
import CancelIcon from "~icons/ic/baseline-cancel";
import AiModeIcon from "@/components/shared/AiModeIcon.vue";
import { showAiModeOption } from "@/config/feature-toggles";
import AiGlowBorder from "@/components/shared/AiGlowBorder.vue";
import MaterialSymbolsEventUpcoming from "~icons/material-symbols/event-upcoming";
import ApartmentIcon from "~icons/ic/baseline-apartment";
import MaterialSymbolsTrophyOutlineSharp from "~icons/material-symbols/trophy-outline-sharp";
import MaterialSymbolsStadiumOutline from "~icons/material-symbols/stadium-outline";
import MaterialSymbolsApparelOutline from "~icons/material-symbols/apparel-outline";
import { useRoute } from "vue-router";
import { useEventListener, refDebounced } from "@vueuse/core";
import { useBreakpoints } from "@xs2event/js-lib/util";
import { sportTypes } from "@xs2event/js-lib/util";
import { useFeaturedSports } from "@/composables/strapi/api/brands";
import { useSuggestedSearches } from "./useSuggestedSearches";
import { shop } from "@/brandconfig";
import { toEvents } from "@/components/events";
import { SportType } from "@xs2event/js-lib/api";
import { eventsWithCity, eventsWithTeam, eventsWithTournament, eventsWithVenue } from "@/constants/routes";

const hasFocus = ref(false);
const aiMode = ref(false);
const aiDialogOpen = ref(false);
const pendingResultClick = ref(false);
const rootElement = ref<HTMLElement>();
const { t, locale } = useI18n();

const route = useRoute();

// `lg` is the same breakpoint PageHeader uses to swap its desktop search bar for the mobile one,
// so the narrow placeholder lands exactly on the instance that is short of room.
const { lg: isDesktop } = useBreakpoints();
const placeholder = computed(() => {
  if (aiMode.value) return t("Ask anything");
  return isDesktop.value ? t("Search events, teams, cities..") : t("Search...");
});

const DEFAULT_SPORT_COUNT = 8;

// Branding configs hold raw strings, so drop anything js-lib has no icon and name for, along with
// the catch-all "other".
const brandSportTypes = (brandconfig.SPORT_TYPES ?? []).filter(
  (key: string): key is SportType => key !== SportType.Other && key in sportTypes
);

// The same Strapi-managed set the home page shows under "Other sports"; a brand with none
// configured there -- or a fetch that failed -- falls back to its own sport types. A brand that
// sells a single sport has nothing to choose between, so it drops the section entirely.
const { data: featuredSportKeys } = useFeaturedSports(shop.key);
const featuredSports = computed(() => {
  if (brandSportTypes.length <= 1) return [];

  const keys: string[] = featuredSportKeys.value?.length
    ? featuredSportKeys.value
    : brandSportTypes.slice(0, DEFAULT_SPORT_COUNT);

  return keys.filter((key): key is SportType => key in sportTypes).map((key) => ({ key, ...sportTypes[key] }));
});

// The classic results panel. AI mode replaces it with the dialog rather than stacking on top of it.
const panelOpen = computed(() => hasFocus.value && !aiMode.value);

// The card around the panel: it opens with the panel but only closes once the panel body has faded
// out, so the body never fades over a card that has already dropped its background.
const panelShown = ref(false);
watch(panelOpen, (open) => open && (panelShown.value = true));
// A leave cut short by the panel opening again still ends in after-leave (e.g. switching browser tabs:
// the blur starts the fade, the hidden tab stalls it, and refocusing on return reopens the panel), so
// only drop the card if the panel is still closed.
const onPanelLeft = () => {
  if (!panelOpen.value) panelShown.value = false;
};

// The page behind the open panel stays put: scrolling there would only drag the page under the
// backdrop. `html` is the element that scrolls (see style.css). Padding takes the place of the
// scrollbar that goes away, so the page doesn't shift sideways while it is locked.
const lockPageScroll = (locked: boolean) => {
  const html = document.documentElement;
  html.style.paddingRight = locked ? `${window.innerWidth - html.clientWidth}px` : "";
  html.style.overflow = locked ? "hidden" : "";
};
watch(panelOpen, lockPageScroll);
// Navigating away with the panel open would otherwise leave the page locked.
onBeforeUnmount(() => panelOpen.value && lockPageScroll(false));

const { suggestions: suggestedSearches, loading: suggestionsLoading } = useSuggestedSearches(panelOpen);

watch(
  () => ({ ...route }),
  () => {
    // Results keep focus on the input (mousedown.prevent), so following one leaves it focused with the
    // panel closed. Blur it, or clicking the bar again fires no focusin and the panel stays shut.
    const active = document.activeElement as HTMLElement | null;
    if (active && rootElement.value?.contains(active)) active.blur();
    query.value = "";
    hasFocus.value = false;
    aiDialogOpen.value = false;
  },
  { deep: true }
);

useEventListener("keydown", (e: KeyboardEvent) => {
  if (!hasFocus.value) return;

  if (e.key === "Escape") {
    close();
  }
});

// Flipping the toggle opens or closes the dialog outright; with AI mode already on, reaching for
// the search bar opens it too, since that is what search means in this mode.
watch(aiMode, (on) => (aiDialogOpen.value = on));
watch(hasFocus, (focused) => {
  if (focused && aiMode.value) aiDialogOpen.value = true;
});

// Dismissing the dialog leaves search entirely rather than falling through to the classic panel,
// which would otherwise sit focused underneath and re-open the dialog on the spot.
const closeAiDialog = () => {
  aiDialogOpen.value = false;
  close();
};

// No AI search backend yet: the prompt closes the dialog and falls back to the classic query so it
// is never a dead end. Swap this for the AI search call once it exists.
const onAiPrompt = (prompt: string) => {
  aiDialogOpen.value = false;
  query.value = prompt;
};

// The keyboard's search key (enterkeyhint) has nothing to submit, since results already follow the
// query as it is typed. On mobile it dismisses the keyboard like Google does, which onFocusOut
// treats the same as the "Done" checkmark, so the results stay on screen.
const onSearchKey = (e: KeyboardEvent) => {
  if (e.isComposing || isDesktop.value) return;
  (e.target as HTMLInputElement).blur();
};

const onFocusOut = (e: FocusEvent) => {
  if (pendingResultClick.value || rootElement.value?.contains(e.relatedTarget as Node | null)) return;
  // On mobile the panel takes over the screen and has its own back button. A blur that doesn't move
  // focus anywhere is the keyboard being dismissed (e.g. the iOS "Done" checkmark), so the panel stays.
  if (!isDesktop.value && !e.relatedTarget) return;
  hasFocus.value = false;
};

const close = () => {
  (document.activeElement as HTMLElement)?.blur();
  hasFocus.value = false;
  query.value = "";
};

const filterIcons = {
  event: MaterialSymbolsEventUpcoming,
  city: ApartmentIcon,
  team: MaterialSymbolsApparelOutline,
  tournament: MaterialSymbolsTrophyOutlineSharp,
  venue: MaterialSymbolsStadiumOutline,
} as const;

const search = snakeToCamel(brandconfig.search);

// Each type is fetched up to SEARCH_PAGE_SIZE deep; "All" previews the first few of each and
// leaves the rest to that type's own tab.
const SEARCH_PAGE_SIZE = 50;
const ALL_TAB_LIMIT = 5;
// A single character matches nearly everything, so the search only starts from two.
const MIN_QUERY_LENGTH = 2;

const query = ref<string>("");
const debouncedQuery = refDebounced(query, 400);

const toSearchTerm = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length >= MIN_QUERY_LENGTH ? trimmed : "";
};
// What the queries search for, and what is typed right now: they differ while the debounce runs.
const searchTerm = computed(() => toSearchTerm(debouncedQuery.value));
const typedTerm = computed(() => toSearchTerm(query.value));
const searchEnabled = computed(() => !!searchTerm.value);

const isLoading = computed(() => {
  return (
    typedTerm.value !== searchTerm.value ||
    teamsQuery.isLoading.value ||
    citiesQuery.isLoading.value ||
    tournamentsQuery.isLoading.value ||
    venuesQuery.isLoading.value ||
    eventsQuery.isLoading.value
  );
});

const teamsQuery = useTeams(
  computed(() => ({
    teamName: (searchTerm.value && `ilike:${searchTerm.value}`) || undefined,
    sorting: "team_name:asc",
    pageSize: SEARCH_PAGE_SIZE,
    eventStartdate: `ge:${new Date().toISOString().split("T")[0]}`,
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: searchEnabled, debounce: false }
);

const citiesQuery = useCities(
  computed(() => ({
    query: (searchTerm.value && `ilike:${searchTerm.value}`) || undefined,
    pageSize: SEARCH_PAGE_SIZE,
  })),
  { enabled: searchEnabled, debounce: false }
);

// Tournaments come from the portal tournaments cache, the same one the suggestions read, filtered
// here the way the `/tournaments` search did: name contains the term, the brand's sport if it has
// one, first 50 by name. The cache holds only tournaments still running, which the search asked
// for as well. Without a slug a tournament has no page to link to.
const tournamentsQuery = usePortalTournamentsCache({
  enabled: panelOpen,
  staleTime: PORTAL_TOURNAMENTS_CACHE_STALE_TIME,
});
const searchSportType = search.params?.sportType;
const matchedTournaments = computed(() => {
  const term = searchTerm.value.toLowerCase();
  if (!term) return [];

  return (tournamentsQuery.data.value?.tournaments ?? [])
    .filter(
      (tournament) =>
        !!tournament.slug &&
        tournament.officialName.toLowerCase().includes(term) &&
        (!searchSportType || tournament.sportType === searchSportType)
    )
    .sort((a, b) => a.officialName.localeCompare(b.officialName))
    .slice(0, SEARCH_PAGE_SIZE);
});

const venuesQuery = useVenues(
  computed(() => ({
    venueName: (searchTerm.value && `ilike:${searchTerm.value}`) || undefined,
    pageSize: SEARCH_PAGE_SIZE,
    eventStopdate: "ge:" + todayISODate,
    sorting: "official_name:asc",
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: searchEnabled, debounce: false }
);

const eventsQuery = useEventsSearch(
  computed(() => ({
    query: searchTerm.value,
    pageSize: SEARCH_PAGE_SIZE,
  })),
  { enabled: searchEnabled, debounce: false }
);

const events = computed(() => eventsQuery.data.value?.events || []);

// A search hit carries only the basics: no teams, sport or location. The full events fill those in,
// from the portal events cache where it has them; cards render from the hit straight away and pick
// the rest up as it arrives.
const eventIds = computed(() => events.value.map((event) => event.eventId));
const { data: fullEventsList } = useCachedEventsByIds(eventIds, panelOpen);
const fullEvents = computed(() => new Map(fullEventsList.value.map((event) => [event.eventId, event])));

const eventCards = computed(() =>
  events.value.map((event) => {
    const full = fullEvents.value.get(event.eventId);
    return camelToSnake({ ...event, ...full });
  })
);

type ResultLink = { name: string; subtitle: string; colorsSvg?: string; to: ReturnType<typeof eventsWithTeam> };
type ResultGroup = { key: string; label: string; icon: Component; count: number; results: ResultLink[] };

const byName = (a: ResultLink, b: ResultLink) => a.name.localeCompare(b.name);

// Result context reads as "Football · Italy": the parts that exist, joined with a middle dot.
const joinParts = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(" · ");
const sportName = (sportType: string) => t(lookupSportTypeName(sportType));
// Falls back to the raw value when it is not a known ISO code, except "WORLD" (a tournament region
// for global tournaments), which has no place to name.
const countryName = (isoCountry?: string) => {
  if (!isoCountry || isoCountry === "WORLD") return undefined;
  return lookupCountryName(isoCountry, locale.value) ?? isoCountry;
};

// One group per result type, in the order the tabs and the "All" sections list them. Types without
// results drop out, so neither shows an empty tab or section.
const resultGroups = computed<ResultGroup[]>(() => {
  const teams = (teamsQuery.data.value?.teams ?? []).map((x) => ({
    name: x.officialName,
    subtitle: joinParts(sportName(x.sportType), countryName(x.isoCountry)),
    colorsSvg: x.colorsSvg,
    to: eventsWithTeam(x.slug.toLowerCase(), { sport_type: x.sportType == "soccer" ? "football" : x.sportType }),
  }));
  const tournaments = matchedTournaments.value.map((x) => ({
    name: `${x.officialName} (${x.season})`,
    subtitle: joinParts(sportName(x.sportType), countryName(x.region)),
    to: eventsWithTournament(x.slug!.toLowerCase()),
  }));
  const cities = (citiesQuery.data.value?.cities ?? []).map((x) => ({
    name: x.city,
    subtitle: countryName(x.country) ?? "",
    to: eventsWithCity(x.city.toLowerCase().replace(" ", "-")),
  }));
  const venues = (venuesQuery.data.value?.venues ?? []).map((x) => ({
    name: x.officialName,
    subtitle: [x.city, countryName(x.country)].filter(Boolean).join(", "),
    to: eventsWithVenue(x.slug.toLowerCase()),
  }));

  const groups: ResultGroup[] = [
    { key: "events", label: "Events", icon: filterIcons.event, count: events.value.length, results: [] },
    { key: "teams", label: "Teams", icon: filterIcons.team, count: teams.length, results: teams.sort(byName) },
    {
      key: "tournaments",
      label: "Tournaments",
      icon: filterIcons.tournament,
      count: tournaments.length,
      results: tournaments.sort(byName),
    },
    { key: "cities", label: "Cities", icon: filterIcons.city, count: cities.length, results: cities.sort(byName) },
    { key: "venues", label: "Venues", icon: filterIcons.venue, count: venues.length, results: venues.sort(byName) },
  ];
  return groups.filter((group) => group.count > 0);
});

const activeTab = ref("all");

const tabs = computed(() => [
  { key: "all", label: "All", count: resultGroups.value.reduce((sum, group) => sum + group.count, 0) },
  ...resultGroups.value,
]);

const limitForTab = <T,>(items: T[]) => (activeTab.value === "all" ? items.slice(0, ALL_TAB_LIMIT) : items);

const visibleGroups = computed(() =>
  activeTab.value === "all" ? resultGroups.value : resultGroups.value.filter((group) => group.key === activeTab.value)
);

// A new search can leave the open tab without results, so every query starts back on "All".
watch(searchTerm, () => (activeTab.value = "all"));
</script>

<style scoped>
/* Cap the open panel to the space under the header so the results scroll inside it
   rather than running off the bottom of the viewport. */
.search-panel {
  max-height: calc(100svh - var(--spacing) * 48);
}

/* The panel body, timed with the card's own transition. It cannot animate its height -- the card
   is already doing that -- so it fades and settles the last few pixels down. */
.panel-enter-active {
  transition:
    opacity 200ms ease-out,
    transform 200ms ease-out;
}

.panel-leave-active {
  transition:
    opacity 150ms ease-in,
    transform 150ms ease-in;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

/* Same fade as the js-lib Backdrop the cart drawer uses: opacity only, 300ms ease-out in,
   200ms ease-in out. The blur rides along with the opacity rather than animating itself. */
.fade-enter-active {
  transition: opacity 150ms ease-out;
}

.fade-leave-active {
  transition: opacity 150ms ease-in;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
